import { ClipboardEvent, FocusEvent, KeyboardEvent, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Box, FormHelperText, theme } from '@sbv-clients/ui-shared';

import { useFormFieldError } from '../../hooks/use-field-error';

const defaultLabelSx = {
  fontSize: '13px',
  fontWeight: 500,
  color: theme.custom?.stepText,
};

const LENGTH = 6;

// The one-time passcode as six digit boxes. They read and write the same
// `otp` form value the single text field used, so the schema and submit are
// unchanged; the form validates it when focus leaves the boxes (onBlur mode).
export const FormOtpBoxes = ({
  name = 'otp',
  label = 'One-time passcode',
  labelSx = defaultLabelSx,
}: {
  name?: string;
  label?: string;
  labelSx?: Record<string, unknown>;
}) => {
  const { watch, setValue, trigger } = useFormContext();
  const { error, helperText } = useFormFieldError(name);
  const value = String(watch(name) ?? '');
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const write = (next: string) => {
    const digits = next.replace(/\D/g, '').slice(0, LENGTH);
    setValue(name, digits, { shouldDirty: true, shouldValidate: error });
    return digits;
  };

  const setDigit = (index: number, digit: string) => {
    const chars = value.padEnd(LENGTH, ' ').split('');
    chars[index] = digit || ' ';
    // Keep digits contiguous: the value is what's typed, left to right.
    write(chars.join('').replace(/\s/g, ''));
  };

  const onChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) return;
    setDigit(index, digit);
    refs.current[Math.min(index + 1, LENGTH - 1)]?.focus();
  };

  const onKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (value[index]) {
        setDigit(index, '');
      } else if (index > 0) {
        setDigit(index - 1, '');
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < LENGTH - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = write(e.clipboardData.getData('text'));
    refs.current[Math.min(digits.length, LENGTH - 1)]?.focus();
  };

  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      trigger(name);
    }
  };

  return (
    <Box>
      <Box
        component="label"
        htmlFor={`${name}-0`}
        sx={{ ...labelSx, display: 'block', mb: '6px' }}
      >
        {label}
      </Box>
      <Box
        role="group"
        aria-label={label}
        onBlur={onBlur}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${LENGTH}, minmax(0, 1fr))`,
          gap: '8px',
        }}
      >
        {Array.from({ length: LENGTH }, (_, i) => (
          <Box
            key={i}
            component="input"
            id={`${name}-${i}`}
            ref={(el: HTMLInputElement | null) => {
              refs.current[i] = el;
            }}
            value={value[i] ?? ''}
            inputMode="numeric"
            maxLength={1}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${i + 1}`}
            aria-invalid={error || undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(i, e.target.value)
            }
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => onKeyDown(i, e)}
            onPaste={onPaste}
            onFocus={(e: FocusEvent<HTMLInputElement>) => e.target.select()}
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              height: 48,
              border: `1px solid ${
                error
                  ? theme.custom?.fieldErrorBorder
                  : theme.custom?.controlBorder
              }`,
              borderRadius: '8px',
              background: theme.background.white,
              textAlign: 'center',
              font: 'inherit',
              fontSize: '20px',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: theme.custom?.brandNavy,
              outline: 'none',
              '&:focus': {
                borderColor: theme.colors.primary.main,
                boxShadow: `0 0 0 3px ${theme.custom?.listRowSelectedBg}`,
              },
            }}
          />
        ))}
      </Box>
      {error && <FormHelperText error>{helperText}</FormHelperText>}
    </Box>
  );
};
